'use client';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  mobileGraphql,
  mobileKeys,
  type MobileConnection,
  type MobileFrontEndBubbleMessageView,
  type MobileUiConfigArtifact,
  type MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import {
  buildParentThreadListQuery,
  buildThreadWindowQuery,
  messageRootQuery,
} from '../lib/build-message-query';
import { buildFieldSelection } from '../lib/selection-set';
import { ARTIFACT_ID_FIELD } from '../lib/constants';
import { isGraphQLName } from '../lib/gql-literal';
import { timestampMillis } from '../lib/timestamps';
import {
  mergeParticipantLabels,
  parentPartySelectionFields,
  participantLabelsFromPartyList,
  participantsFromMessageRow,
  resolveConversationTitle,
  resolvePath,
  resolveThreadKey,
  threadKeyFields,
  threadParticipantSelectionFields,
  type MessageThread,
} from '../lib/message-mapping';

/** Rows scanned to group ``by_field`` threads client-side (first-cut enumeration). */
const THREAD_WINDOW_LIMIT = 1000;
/** Parent (thread) artifacts fetched for ``by_parent`` threading. */
const PARENT_THREAD_LIMIT = 500;

function asString(value: unknown): string {
  if (value == null) return '';
  return typeof value === 'string' ? value : String(value);
}

/** Threads sort newest-first, so unknown activity sorts last (NEGATIVE_INFINITY). */
const MISSING_ACTIVITY = Number.NEGATIVE_INFINITY;

interface UseMessageThreadsArgs {
  mobileAcquisitionId: number;
  view: MobileFrontEndBubbleMessageView;
  artifact: MobileUiConfigArtifact;
  uiConfig: MobileUiConfigResponse;
  /** When false the query stays idle. Defaults to on. */
  active?: boolean;
}

/**
 * Enumerate the selectable conversation threads for a message artifact.
 *
 * ``by_parent`` lists the parent (thread) artifact type directly and enriches
 * titles from parent parties plus a window of messages. ``by_field`` scans a
 * window of messages and groups them by ``thread.key_field`` on the client.
 * Titles prefer other participants (name, then phone, then id) over thread ids.
 */
export function useMessageThreads(
  args: UseMessageThreadsArgs,
): UseQueryResult<MessageThread[], Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();
  const { mobileAcquisitionId, view, artifact, uiConfig } = args;
  const thread = view.thread;

  const parentArtifact =
    thread.mode === 'by_parent' && thread.parent_graphql_type
      ? uiConfig.artifacts[thread.parent_graphql_type]
      : undefined;

  const rootQuery =
    thread.mode === 'by_parent'
      ? (() => {
          const name = parentArtifact?.front_end?.graphql_root_query;
          return name && isGraphQLName(name) ? name : null;
        })()
      : messageRootQuery(artifact);
  const endpoint = thread.mode === 'by_parent' ? (parentArtifact?.endpoint ?? artifact.endpoint) : artifact.endpoint;

  return useQuery<MessageThread[], Error>({
    queryKey: mobileKeys.messageThreads(cellId, mobileAcquisitionId, artifact.graphql_type, {
      mode: thread.mode,
    }),
    enabled: enabled && args.active !== false && Boolean(rootQuery),
    queryFn: async ({ signal }) => {
      if (thread.mode === 'by_parent') {
        const partySelection = parentPartySelectionFields(parentArtifact, uiConfig);
        const selectionFields = [ARTIFACT_ID_FIELD];
        if (thread.parent_title_field) selectionFields.push(thread.parent_title_field);
        if (thread.parent_subtitle_field) selectionFields.push(thread.parent_subtitle_field);
        if (partySelection) selectionFields.push(...partySelection.paths);

        const parentQuery = buildParentThreadListQuery({
          rootQuery: rootQuery as string,
          mobileAcquisitionId,
          offset: 0,
          limit: PARENT_THREAD_LIMIT,
          selection: buildFieldSelection(selectionFields),
        });
        if (!parentQuery) throw new Error('Invalid thread root query');

        const messageRoot = messageRootQuery(artifact);
        const messageSelection = [
          ARTIFACT_ID_FIELD,
          ...(thread.parent_link_field ? [thread.parent_link_field] : []),
          view.timestamp_field,
          ...threadParticipantSelectionFields(view, artifact),
        ];
        const messageQuery = messageRoot
          ? buildThreadWindowQuery({
              rootQuery: messageRoot,
              mobileAcquisitionId,
              offset: 0,
              limit: THREAD_WINDOW_LIMIT,
              selection: buildFieldSelection(messageSelection),
            })
          : null;

        const [parentData, messageData] = await Promise.all([
          mobileGraphql<Record<string, MobileConnection>>({
            cellId: cellId as string,
            accessToken: accessToken as string,
            endpoint,
            query: parentQuery,
            signal,
          }),
          messageQuery
            ? mobileGraphql<Record<string, MobileConnection>>({
                cellId: cellId as string,
                accessToken: accessToken as string,
                endpoint: artifact.endpoint,
                query: messageQuery,
                signal,
              })
            : Promise.resolve(null),
        ]);

        const connection = parentData[rootQuery as string];
        if (!connection) throw new Error(`Missing "${rootQuery}" in mobile-service response`);

        const participantsByKey = new Map<string, string[]>();
        const messageConnection = messageRoot && messageData ? messageData[messageRoot] : undefined;
        for (const row of messageConnection?.items ?? []) {
          const key = resolveThreadKey(row, view);
          if (key == null || key === '') continue;
          participantsByKey.set(
            key,
            mergeParticipantLabels(participantsByKey.get(key) ?? [], participantsFromMessageRow(row, view)),
          );
        }

        return connection.items.map((row) => {
          const key = asString(resolvePath(row, ARTIFACT_ID_FIELD));
          const configuredTitle = thread.parent_title_field
            ? asString(resolvePath(row, thread.parent_title_field))
            : '';
          const subtitle = thread.parent_subtitle_field
            ? asString(resolvePath(row, thread.parent_subtitle_field))
            : '';
          const fromParent = partySelection
            ? participantLabelsFromPartyList(resolvePath(row, partySelection.listField))
            : [];
          const participants = mergeParticipantLabels(fromParent, participantsByKey.get(key) ?? []);
          return {
            key,
            title: resolveConversationTitle(key, configuredTitle, participants),
            subtitle: subtitle || null,
          } satisfies MessageThread;
        });
      }

      // by_field: scan a window of messages and group client-side.
      const selectionFields = [
        ARTIFACT_ID_FIELD,
        ...threadKeyFields(view),
        view.timestamp_field,
        ...threadParticipantSelectionFields(view, artifact),
      ];
      if (thread.title_field) selectionFields.push(thread.title_field);
      if (thread.subtitle_field) selectionFields.push(thread.subtitle_field);
      const query = buildThreadWindowQuery({
        rootQuery: rootQuery as string,
        mobileAcquisitionId,
        offset: 0,
        limit: THREAD_WINDOW_LIMIT,
        selection: buildFieldSelection(selectionFields),
      });
      if (!query) throw new Error('Invalid thread root query');
      const data = await mobileGraphql<Record<string, MobileConnection>>({
        cellId: cellId as string,
        accessToken: accessToken as string,
        endpoint,
        query,
        signal,
      });
      const connection = data[rootQuery as string];
      if (!connection) throw new Error(`Missing "${rootQuery}" in mobile-service response`);

      const byKey = new Map<string, MessageThread & { configuredTitle: string; participants: string[] }>();
      for (const row of connection.items) {
        const key = resolveThreadKey(row, view);
        if (key == null || key === '') continue;
        const configuredTitle = thread.title_field ? asString(resolvePath(row, thread.title_field)) : '';
        const subtitle = thread.subtitle_field
          ? asString(resolvePath(row, thread.subtitle_field))
          : '';
        const participants = participantsFromMessageRow(row, view);
        const activity = resolvePath(row, view.timestamp_field);
        const existing = byKey.get(key);
        if (existing) {
          existing.count = (existing.count ?? 0) + 1;
          existing.participants = mergeParticipantLabels(existing.participants, participants);
          if (!existing.configuredTitle && configuredTitle) existing.configuredTitle = configuredTitle;
          if (!existing.subtitle && subtitle) existing.subtitle = subtitle;
          existing.title = resolveConversationTitle(key, existing.configuredTitle, existing.participants);
          if (
            timestampMillis(activity, MISSING_ACTIVITY) >
            timestampMillis(existing.lastActivity, MISSING_ACTIVITY)
          ) {
            existing.lastActivity = activity == null ? null : String(activity);
          }
        } else {
          byKey.set(key, {
            key,
            configuredTitle,
            participants,
            title: resolveConversationTitle(key, configuredTitle, participants),
            subtitle: subtitle || null,
            lastActivity: activity == null ? null : String(activity),
            count: 1,
          });
        }
      }

      return [...byKey.values()]
        .map(({ configuredTitle: _configuredTitle, participants: _participants, ...thread }) => thread)
        .sort(
          (a, b) =>
            timestampMillis(b.lastActivity, MISSING_ACTIVITY) -
            timestampMillis(a.lastActivity, MISSING_ACTIVITY),
        );
    },
  });
}
